
import React, { CSSProperties, useEffect, useImperativeHandle, useState } from "react";
import QueryBuilder, { ActionElement, ActionWithRulesProps, Combinator, CombinatorSelectorProps, Field, RuleGroupType, Translations, ValueEditorType, ValueSelector } from "react-querybuilder";
import { deepEqual } from "../../common/utils";
import { useTranslation } from "../../hooks";
import 'react-querybuilder/dist/query-builder.scss';
import '../../themes/react-query.scss';


export interface FilterEditorProps {
    query?: RuleGroupType;
    fields: Field[];
    style?: CSSProperties;
    onChange?: (query?: RuleGroupType) => void;
    onValueEditorType?: (field: string, operator: string) => ValueEditorType;
    onEngaged?: (engaged: boolean) => void;
    showCombinatorsBetweenRules?: boolean;
    hideGroups?: boolean;
    translations?: Partial<Translations>;
    disabled?: boolean;
}

export interface FilterEditorRef {
    save(): RuleGroupType;
}

/**
 * Creates an empty rule group used to bootstrap the query builder state.
 * @returns A base rule group configured with an empty rule array and default combinator.
 */
export const initQuery = () => {
    let query: RuleGroupType = {
        combinator: "and",
        rules: [],
        id: "new"
    }
    return query;
}

/**
 * Wraps the react-querybuilder control to provide Fabrix-specific defaults and translations.
 * @param props - Configuration for available fields, translation overrides, and event handlers.
 * @param ref - Ref that exposes an imperative save call to retrieve the current query.
 * @returns A query builder layout that tracks engagement and relays updates to consumers.
 */
const FilterEditor = React.forwardRef((props: FilterEditorProps, ref) => {

    const translate = useTranslation();
    const [_query, setQuery] = useState<RuleGroupType>(initQuery)
    const [_fields, setFields] = useState<Field[]>([])
    const [_engaged, setEngaged] = useState<boolean>(false)

    useEffect(() => {
        if (props.onEngaged)
            props.onEngaged(_engaged)

        if (_engaged && props.onChange) {
            if (!deepEqual(props.query, _query))
                props.onChange(_query)
        }

    }, [_engaged, _query])

    useEffect(() => {
        setQuery(props.query ?? initQuery())
    }, [props.query])



    /**
     * Exposes the latest query definition to parent components via the provided ref.
     */
    useImperativeHandle(ref, () => ({
        save() {
            if (_query) {
                return _query
            }
            return null;
        }
    }));

    useEffect(() => {
        setFields(props.fields)
    }, [props.fields]);


    /**
     * Updates local query state when the builder emits changes while preventing redundant writes.
     * @param query - The updated rule group emitted by the react-querybuilder component.
     */
    const handleQueryChanged = (query: RuleGroupType) => {
        let isEqual = deepEqual(query, _query)
        if (!isEqual)
            setQuery(query)
    }

    const translations: Partial<Translations> = {
        addRule: { label: translate("FILTER_ADD_RULE_LABEL"), title: translate("FILTER_ADD_RULE_TITLE") },
        addGroup: { label: translate("FILTER_ADD_GROUP_LABEL"), title: translate("FILTER_ADD_GROUP_TITLE") },
        removeRule: { label: "x", title: translate("FILTER_REMOVE_RULE_TILE") },
        removeGroup: { label: "x", title: translate("FILTER_REMOVE_GROUP_TILE") },
        ...props.translations
    }

    const combinators = [{ name: 'and', label: translate("FILTER_COMBINATOR_AND") }, { name: 'or', label: translate("FILTER_COMBINATOR_OR") }]

    let style = { width: "100%", height: "100%", ...props.style }

      
      
    return (
        <div style={style} onClick={() => setEngaged(true)} onMouseEnter={() => setEngaged(true)}>
            <QueryBuilder fields={_fields} query={_query} 
                showCombinatorsBetweenRules={props.showCombinatorsBetweenRules ?? true}
                getValueEditorType={props.onValueEditorType}
                translations={translations}
                combinators={combinators}
                onQueryChange={handleQueryChanged}
                controlClassnames={{ queryBuilder: `queryBuilder-branches ${props.hideGroups ? "ruleGroup-hide" : ""}` }} />
        </div>


    )
})

export default FilterEditor

